import React, { useState, useEffect } from 'react';
import axios from 'axios';

const PressReleases = () => {
    const [pressReleases, setPressReleases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        summary: '',
        content: '',
        published_date: new Date().toISOString().split('T')[0],
        is_active: true,
        is_featured: false,
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        fetchPressReleases();
    }, []);

    const fetchPressReleases = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('admin_token');
            console.log('Fetching press releases...');
            
            const response = await axios.get('https://pitac.gov.pk/api/admin/press-releases', {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                }
            });
            
            console.log('Press releases response:', response.data);
            setPressReleases(response.data || []);
            setError('');
        } catch (error) {
            console.error('Error fetching press releases:', error);
            if (error.response) {
                console.log('Error status:', error.response.status);
                console.log('Error data:', error.response.data);
            }
            setError('Failed to load press releases');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try {
            const token = localStorage.getItem('admin_token');
            
            // Validate required fields
            if (!formData.title.trim()) {
                setError('Title is required');
                setSubmitting(false);
                return;
            }

            if (!formData.published_date) {
                setError('Published date is required');
                setSubmitting(false);
                return;
            }

            let response;
            
            if (editing) {
                // UPDATE: Use JSON for update
                const updateData = {
                    title: formData.title,
                    summary: formData.summary || '',
                    content: formData.content || '',
                    published_date: formData.published_date,
                    is_active: formData.is_active ? 1 : 0,
                    is_featured: formData.is_featured ? 1 : 0,
                };

                console.log('Updating press release:', editing.id, updateData);

                // If there's a new image, use FormData
                if (imageFile) {
                    const formDataToSend = new FormData();
                    Object.keys(updateData).forEach(key => {
                        formDataToSend.append(key, updateData[key]);
                    });
                    formDataToSend.append('image', imageFile);

                    response = await axios.post(
                        `https://pitac.gov.pk/api/admin/press-releases/${editing.id}?_method=PUT`,
                        formDataToSend,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'multipart/form-data',
                                'Accept': 'application/json',
                            }
                        }
                    );
                } else {
                    response = await axios.put(
                        `https://pitac.gov.pk/api/admin/press-releases/${editing.id}`,
                        updateData,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json',
                                'Accept': 'application/json',
                            }
                        }
                    );
                }
                console.log('Update response:', response.data);
            } else {
                // CREATE: Use FormData for new press release
                const formDataToSend = new FormData();
                formDataToSend.append('title', formData.title);
                formDataToSend.append('summary', formData.summary || '');
                formDataToSend.append('content', formData.content || '');
                formDataToSend.append('published_date', formData.published_date);
                formDataToSend.append('is_active', formData.is_active ? '1' : '0');
                formDataToSend.append('is_featured', formData.is_featured ? '1' : '0');
                
                if (imageFile) {
                    formDataToSend.append('image', imageFile);
                }

                console.log('Creating new press release');
                response = await axios.post(
                    'https://pitac.gov.pk/api/admin/press-releases',
                    formDataToSend,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data',
                            'Accept': 'application/json',
                        }
                    }
                );
                console.log('Create response:', response.data);
            }

            await fetchPressReleases();
            resetForm();
            alert(editing ? 'Press release updated successfully!' : 'Press release created successfully!');
            
        } catch (error) {
            console.error('Error saving press release:', error);
            
            if (error.response) {
                console.log('Error response data:', error.response.data);
                console.log('Error response status:', error.response.status);
                
                if (error.response.status === 422) {
                    const errors = error.response.data.errors || {};
                    const messages = Object.values(errors).flat().join(', ');
                    setError(`Validation error: ${messages}`);
                } else {
                    setError(error.response.data?.message || `Error: ${error.response.status}`);
                }
            } else if (error.request) {
                setError('No response from server. Make sure the backend is running.');
            } else {
                setError(error.message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this press release?')) {
            try {
                const token = localStorage.getItem('admin_token');
                await axios.delete(`https://pitac.gov.pk/api/admin/press-releases/${id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                fetchPressReleases();
                alert('Press release deleted successfully!');
            } catch (error) {
                console.error('Error deleting press release:', error);
                alert('Failed to delete press release');
            }
        }
    };

    const handleEdit = (press) => {
        setEditing(press);
        setFormData({
            title: press.title || '',
            summary: press.summary || '',
            content: press.content || '',
            published_date: press.published_date || new Date().toISOString().split('T')[0],
            is_active: press.is_active === 1 || press.is_active === true,
            is_featured: press.is_featured === 1 || press.is_featured === true,
        });
        setImagePreview(press.image);
        setImageFile(null);
        setError('');
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            title: '',
            summary: '',
            content: '',
            published_date: new Date().toISOString().split('T')[0],
            is_active: true,
            is_featured: false,
        });
        setImageFile(null);
        setImagePreview(null);
        setEditing(null);
        setShowModal(false);
        setError('');
        setSubmitting(false);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    if (loading) return <div className="admin-loading">Loading press releases...</div>;

    return (
        <div>
            <div className="admin-header">
                <h2>Press Releases Management ({pressReleases.length})</h2>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button 
                        className="admin-add-btn" 
                        onClick={fetchPressReleases}
                        style={{ background: '#6b7280' }}
                    >
                        🔄 Refresh
                    </button>
                    <button className="admin-add-btn" onClick={() => setShowModal(true)}>
                        + Add New Press Release
                    </button>
                </div>
            </div>

            {error && (
                <div style={{ 
                    background: '#fef2f2', 
                    color: '#dc2626', 
                    padding: '0.8rem 1rem', 
                    borderRadius: '8px', 
                    marginBottom: '1rem',
                    border: '1px solid #fecaca'
                }}>
                    <strong>Error:</strong> {error}
                    <button 
                        onClick={fetchPressReleases}
                        style={{ 
                            marginLeft: '1rem', 
                            padding: '0.3rem 1rem',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}

            {pressReleases.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7a8f' }}>
                    <p>No press releases found. Click "Add New Press Release" to create one.</p>
                </div>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Date</th>
                                <th>Featured</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pressReleases.map((press) => (
                                <tr key={press.id}>
                                    <td><strong>{press.title}</strong></td>
                                    <td>{press.published_date}</td>
                                    <td>{press.is_featured ? '⭐ Featured' : ''}</td>
                                    <td>
                                        <span className={`admin-status-badge ${press.is_active ? 'admin-status-active' : 'admin-status-inactive'}`}>
                                            {press.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="admin-actions">
                                            <button className="admin-edit-btn" onClick={() => handleEdit(press)}>Edit</button>
                                            <button className="admin-delete-btn" onClick={() => handleDelete(press.id)}>Delete</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="admin-modal-overlay" onClick={() => resetForm()}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2>{editing ? 'Edit Press Release' : 'Add New Press Release'}</h2>
                            <button className="admin-modal-close" onClick={resetForm}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            {error && (
                                <div style={{ 
                                    background: '#fef2f2', 
                                    color: '#dc2626', 
                                    padding: '0.8rem', 
                                    borderRadius: '8px', 
                                    marginBottom: '1rem',
                                    border: '1px solid #fecaca'
                                }}>
                                    {error}
                                </div>
                            )}
                            
                            <div className="admin-form-group">
                                <label>Title *</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    required
                                    placeholder="Enter press release title"
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Summary</label>
                                <textarea
                                    value={formData.summary}
                                    onChange={(e) => setFormData({...formData, summary: e.target.value})}
                                    rows="3"
                                    placeholder="Brief summary"
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Content</label>
                                <textarea
                                    value={formData.content}
                                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                                    rows="5"
                                    placeholder="Full content"
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Published Date *</label>
                                <input
                                    type="date"
                                    value={formData.published_date}
                                    onChange={(e) => setFormData({...formData, published_date: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Image</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                                {imagePreview && (
                                    <div style={{ marginTop: '10px' }}>
                                        <img 
                                            src={imagePreview} 
                                            alt="Preview" 
                                            className="admin-image-preview"
                                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                                        />
                                    </div>
                                )}
                                {editing && !imageFile && imagePreview && (
                                    <div style={{ marginTop: '5px', fontSize: '0.8rem', color: '#6b7a8f' }}>
                                        Current image shown. Upload new to replace.
                                    </div>
                                )}
                            </div>
                            <div className="admin-form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                    />
                                    Active
                                </label>
                            </div>
                            <div className="admin-form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.is_featured}
                                        onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                                    />
                                    Featured
                                </label>
                            </div>
                            <button 
                                type="submit" 
                                className="admin-submit-btn"
                                disabled={submitting}
                            >
                                {submitting ? 'Saving...' : (editing ? 'Update Press Release' : 'Create Press Release')}
                            </button>
                            <button 
                                type="button" 
                                onClick={resetForm}
                                style={{ 
                                    marginTop: '0.5rem',
                                    padding: '0.5rem',
                                    background: 'transparent',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    width: '100%',
                                    color: '#6b7a8f'
                                }}
                            >
                                Cancel
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PressReleases;